<template>
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="open && !effectivePinned" class="fixed inset-0 z-40 bg-black/30" @click="close()" />
    </Transition>
  </Teleport>

  <Transition :name="effectivePinned ? 'drawer-push' : 'playlist-slide'">
    <div
      v-if="open"
      class="glass-surface flex flex-col bg-white dark:bg-gray-800 overflow-hidden pt-safe pb-safe"
      :class="[
        effectivePinned
          ? 'flex-shrink-0 h-screen sticky top-0 border-l border-gray-200 dark:border-gray-700 shadow-lg'
          : 'fixed z-50 border border-gray-200 dark:border-gray-700 shadow-2xl',
        !effectivePinned && !isMobile ? 'rounded-2xl' : '',
      ]"
      :style="panelStyle">
      <div
        class="glass-header flex items-center gap-2.5 px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 flex-shrink-0">
        <div class="p-1.5 rounded-lg bg-primary-600 flex-shrink-0">
          <span class="material-symbols-outlined text-white" style="font-size: 18px">queue_music</span>
        </div>
        <div class="min-w-0 flex-1">
          <p class="text-sm font-semibold text-gray-900 dark:text-white leading-tight">
            {{ tt('Playlists', 'Predvajalni seznami') }}
          </p>
          <p class="text-xs text-gray-500 dark:text-gray-400">
            {{
              tt(
                `${playlists.length} saved`,
                `${playlists.length} ${slCount(playlists.length, 'shranjen', 'shranjena', 'shranjene', 'shranjenih')}`,
              )
            }}
          </p>
        </div>
        <button
          v-if="!isMobile"
          @click="ui.togglePlaylistDrawerPin()"
          class="p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 flex-shrink-0"
          :title="effectivePinned ? tt('Unpin', 'Odpni') : tt('Pin', 'Pripni')">
          <span class="material-symbols-outlined text-lg">{{ effectivePinned ? 'push_pin' : 'keep' }}</span>
        </button>
        <button
          v-if="!effectivePinned"
          @click="close()"
          class="p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 flex-shrink-0"
          :title="tt('Close', 'Zapri')">
          <span class="material-symbols-outlined text-lg">close</span>
        </button>
      </div>

      <div class="px-3 py-3 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
        <button
          v-if="!creating"
          @click="creating = true"
          class="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors">
          <span class="material-symbols-outlined text-base">add</span>
          {{ tt('New Playlist', 'Nov seznam predvajanja') }}
        </button>

        <form v-else class="space-y-2" @submit.prevent="createPlaylist">
          <input
            v-model="newName"
            type="text"
            :placeholder="tt('Playlist name', 'Ime seznama predvajanja')"
            class="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
          <div class="flex gap-2">
            <select
              v-model="newIcon"
              class="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
              <option value="queue_music">{{ tt('Queue', 'Čakalna vrsta') }}</option>
              <option value="music_note">{{ tt('Music', 'Glasba') }}</option>
              <option value="movie">{{ tt('Video', 'Video') }}</option>
              <option value="favorite">{{ tt('Favorite', 'Priljubljeno') }}</option>
              <option value="home">{{ tt('Home', 'Domov') }}</option>
            </select>
            <div class="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600 flex-shrink-0">
              <button
                type="button"
                @click="newVisibility = 'private'"
                :class="visibilityButtonClass(newVisibility === 'private')"
                :title="tt('Private', 'Zasebno')">
                <span class="material-symbols-outlined text-base">lock</span>
              </button>
              <button
                type="button"
                @click="newVisibility = 'shared'"
                :class="visibilityButtonClass(newVisibility === 'shared')"
                :title="tt('Shared', 'V skupni rabi')">
                <span class="material-symbols-outlined text-base">groups</span>
              </button>
            </div>
          </div>
          <div class="flex gap-2">
            <button
              type="submit"
              :disabled="!newName.trim() || creatingPlaylist"
              class="flex-1 px-3 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40 transition-colors">
              {{ tt('Save', 'Shrani') }}
            </button>
            <button
              type="button"
              @click="cancelCreate"
              class="px-3 py-2 text-sm rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              {{ tt('Cancel', 'Prekliči') }}
            </button>
          </div>
        </form>
      </div>

      <div class="flex-1 overflow-y-auto min-h-0">
        <!-- Now Playing (shared browser player queue) -->
        <div v-if="queueRows.length" class="border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center gap-2 px-4 pt-3 pb-1">
            <p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide flex-1">
              {{ tt('Now Playing', 'Trenutno predvaja') }}
              <span class="normal-case font-normal text-gray-400/80"
                >·
                {{
                  tt(
                    `${player.playerQueue.value.length} track${player.playerQueue.value.length === 1 ? '' : 's'}`,
                    `${player.playerQueue.value.length} ${slCount(player.playerQueue.value.length, 'skladba', 'skladbi', 'skladbe', 'skladb')}`,
                  )
                }}</span
              >
            </p>
            <button
              @click="clearQueue"
              class="text-[10px] font-medium text-gray-400 hover:text-red-500 transition-colors"
              :title="tt('Clear queue', 'Počisti čakalno vrsto')">
              {{ tt('Clear', 'Počisti') }}
            </button>
          </div>
          <div class="pb-2">
            <button
              v-for="(item, index) in queueRows"
              :key="item.id"
              @click="queuePlay(index)"
              class="w-full flex items-center gap-2 px-4 py-1.5 text-left hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors">
              <span
                class="material-symbols-outlined text-sm flex-shrink-0"
                :class="index === queueIndex ? 'text-primary-500' : 'text-gray-300 dark:text-gray-600'">
                {{
                  index === queueIndex && player.browserPlaying.value
                    ? 'graphic_eq'
                    : index === queueIndex
                      ? 'play_arrow'
                      : 'music_note'
                }}
              </span>
              <div
                class="w-7 h-7 rounded overflow-hidden bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                <img v-if="queueThumb(item)" :src="queueThumb(item)" alt="" class="w-full h-full object-cover" />
                <span v-else class="material-symbols-outlined text-gray-400 text-sm">music_note</span>
              </div>
              <div class="flex-1 min-w-0">
                <p
                  class="text-xs font-medium truncate"
                  :class="
                    index === queueIndex ? 'text-primary-600 dark:text-primary-400' : 'text-gray-900 dark:text-white'
                  ">
                  {{ item.title }}
                </p>
                <p class="text-[11px] text-gray-500 truncate">
                  {{ [item.artist, item.album].filter(Boolean).join(' • ') }}
                </p>
              </div>
              <span class="text-[11px] text-gray-400 tabular-nums flex-shrink-0">{{
                formatDuration(item.duration)
              }}</span>
            </button>
            <p v-if="queueTruncated" class="px-4 py-1 text-[10px] text-gray-400">
              {{
                tt(
                  `+${player.playerQueue.value.length - MAX_QUEUE_ROWS} more in queue`,
                  `+${player.playerQueue.value.length - MAX_QUEUE_ROWS} več v čakalni vrsti`,
                )
              }}
            </p>
          </div>
        </div>

        <div v-if="loading" class="flex justify-center py-8">
          <div class="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        </div>

        <div
          v-else-if="playlists.length === 0"
          class="flex flex-col items-center justify-center py-10 px-6 text-center">
          <span class="material-symbols-outlined text-4xl text-gray-300 dark:text-gray-600">queue_music</span>
          <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {{ tt('No playlists yet', 'Še ni seznamov predvajanja') }}
          </p>
        </div>

        <template v-else>
          <div class="py-2">
            <p
              v-if="privatePlaylists.length"
              class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide px-4 pt-2 pb-1">
              {{ tt('Private', 'Zasebno') }}
            </p>
            <button
              v-for="playlist in privatePlaylists"
              :key="playlist.id"
              @click="playlistStore.selectPlaylist(playlist.id)"
              :class="playlistRowClass(playlist.id)">
              <span class="material-symbols-outlined text-lg flex-shrink-0">{{ playlist.icon || 'queue_music' }}</span>
              <span class="flex-1 min-w-0 text-left">
                <span class="block text-sm font-medium truncate">{{ playlist.name }}</span>
                <span class="block text-xs opacity-70">{{
                  tt(
                    `${playlist.items.length} item${playlist.items.length !== 1 ? 's' : ''}`,
                    `${playlist.items.length} ${slCount(playlist.items.length, 'element', 'elementa', 'elementi', 'elementov')}`,
                  )
                }}</span>
              </span>
              <span class="material-symbols-outlined text-sm opacity-60">lock</span>
            </button>

            <p
              v-if="sharedPlaylists.length"
              class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide px-4 pt-3 pb-1">
              {{ tt('Shared', 'V skupni rabi') }}
            </p>
            <button
              v-for="playlist in sharedPlaylists"
              :key="playlist.id"
              @click="playlistStore.selectPlaylist(playlist.id)"
              :class="playlistRowClass(playlist.id)">
              <span class="material-symbols-outlined text-lg flex-shrink-0">{{ playlist.icon || 'queue_music' }}</span>
              <span class="flex-1 min-w-0 text-left">
                <span class="block text-sm font-medium truncate">{{ playlist.name }}</span>
                <span class="block text-xs opacity-70">{{
                  tt(
                    `${playlist.items.length} item${playlist.items.length !== 1 ? 's' : ''}`,
                    `${playlist.items.length} ${slCount(playlist.items.length, 'element', 'elementa', 'elementi', 'elementov')}`,
                  )
                }}</span>
              </span>
              <span class="material-symbols-outlined text-sm opacity-60">groups</span>
            </button>
          </div>

          <div v-if="selectedPlaylist" class="border-t border-gray-200 dark:border-gray-700">
            <div class="px-4 py-3">
              <div class="flex items-start gap-3">
                <div
                  class="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                  <span class="material-symbols-outlined text-primary-600 dark:text-primary-400">{{
                    selectedPlaylist.icon || 'queue_music'
                  }}</span>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {{ selectedPlaylist.name }}
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ playlistMeta(selectedPlaylist) }}</p>
                </div>
              </div>

              <div class="mt-3 flex items-center gap-2">
                <button
                  @click="playPlaylist(0)"
                  :disabled="selectedPlaylist.items.length === 0"
                  class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 transition-colors"
                  :title="tt('Play', 'Predvajaj')">
                  <span class="material-symbols-outlined text-base">play_arrow</span>
                  {{ tt('Play', 'Predvajaj') }}
                </button>
                <button
                  v-if="isOwner"
                  @click="toggleVisibility"
                  class="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  :title="
                    selectedPlaylist.visibility === 'shared'
                      ? tt('Make private', 'Preklopi v zasebno')
                      : tt('Make shared', 'Preklopi v skupno rabo')
                  ">
                  <span class="material-symbols-outlined text-lg">{{
                    selectedPlaylist.visibility === 'shared' ? 'groups' : 'lock'
                  }}</span>
                </button>
                <button
                  v-if="isOwner"
                  @click="deleteSelectedPlaylist"
                  class="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors ml-auto"
                  :title="tt('Delete', 'Izbriši')">
                  <span class="material-symbols-outlined text-lg">delete</span>
                </button>
              </div>
            </div>

            <div
              v-if="selectedPlaylist.items.length === 0"
              class="px-4 py-8 text-center text-sm text-gray-400 border-t border-gray-100 dark:border-gray-700">
              {{ tt('Empty playlist', 'Prazen seznam predvajanja') }}
            </div>

            <div
              v-else
              class="divide-y divide-gray-100 dark:divide-gray-700 border-t border-gray-100 dark:border-gray-700">
              <div
                v-for="(item, index) in selectedPlaylist.items"
                :key="item.id"
                class="flex items-center gap-2 px-3 py-2.5 group">
                <button
                  @click="playPlaylist(index)"
                  class="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 opacity-90 group-hover:opacity-100 transition-opacity"
                  :title="tt('Play', 'Predvajaj')">
                  <span class="material-symbols-outlined text-sm">play_arrow</span>
                </button>
                <div
                  class="w-9 h-9 rounded overflow-hidden bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                  <img
                    v-if="item.mediaItem?.thumbnailPath || item.mediaItem?.mediaType === 'video'"
                    :src="thumbnailUrl(item.mediaItem.id)"
                    alt=""
                    class="w-full h-full object-cover" />
                  <span v-else class="material-symbols-outlined text-gray-400 text-base">music_note</span>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-xs font-medium text-gray-900 dark:text-white truncate">
                    {{ item.mediaItem?.title || tt('Unknown', 'Neznano') }}
                  </p>
                  <p class="text-xs text-gray-500 truncate">
                    {{ item.mediaItem?.artist || item.mediaItem?.mimeType || '' }}
                  </p>
                </div>
                <span class="text-xs text-gray-400 tabular-nums flex-shrink-0">{{
                  formatDuration(item.mediaItem?.duration)
                }}</span>
                <div v-if="isOwner" class="flex items-center gap-0.5 flex-shrink-0">
                  <button
                    @click="moveItem(index, -1)"
                    :disabled="index === 0"
                    class="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-25"
                    :title="tt('Move up', 'Pomakni gor')">
                    <span class="material-symbols-outlined text-base">keyboard_arrow_up</span>
                  </button>
                  <button
                    @click="moveItem(index, 1)"
                    :disabled="index === selectedPlaylist.items.length - 1"
                    class="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-25"
                    :title="tt('Move down', 'Pomakni dol')">
                    <span class="material-symbols-outlined text-base">keyboard_arrow_down</span>
                  </button>
                  <button
                    @click="playlistStore.removeItem(selectedPlaylist.id, item.id)"
                    class="p-1 rounded text-gray-400 hover:text-red-500"
                    :title="tt('Remove', 'Odstrani')">
                    <span class="material-symbols-outlined text-base">close</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useUIStore } from '@/stores/ui';
import { useAuthStore } from '@/stores/auth';
import { useMediaPlaylistsStore, type MediaPlaylist, type MediaPlaylistVisibility } from '@/stores/mediaPlaylists';
import type { MediaLibraryItem } from '@/stores/mediaPlayers';
import { useBrowserPlayer } from '@/composables/useBrowserPlayer';
import { getMediaThumbnailUrl } from '@/services/mediaLibraryUrls';
import { useI18n } from 'vue-i18n';
import { useMessageBox } from '@/composables/useMessageBox';
import { tt } from '../../utils/consoleText';
import { personName } from '../../utils/personName';

const { t } = useI18n();
const { confirm: confirmBox } = useMessageBox();

const ui = useUIStore();
const authStore = useAuthStore();
const playlistStore = useMediaPlaylistsStore();
const player = useBrowserPlayer();
const { playlists, privatePlaylists, sharedPlaylists, selectedPlaylist, loading } = storeToRefs(playlistStore);

// ── Now Playing (shared browser player queue) ────────────────────────────────
/**
 * Slovene agrees with the count (1 skladba, 2 skladbi, 3 skladbe, 5 skladb);
 * the last two digits decide, as the language does.
 */
function slCount(n: number, one: string, two: string, few: string, many: string): string {
  const lastTwo = n % 100;
  if (lastTwo === 1) return one;
  if (lastTwo === 2) return two;
  if (lastTwo === 3 || lastTwo === 4) return few;
  return many;
}

const MAX_QUEUE_ROWS = 200;
const queueRows = computed(() => player.playerQueue.value.slice(0, MAX_QUEUE_ROWS));
const queueTruncated = computed(() => player.playerQueue.value.length > MAX_QUEUE_ROWS);
const queueIndex = computed(() => player.playerQueueIndex.value);

const creating = ref(false);
const creatingPlaylist = ref(false);
const newName = ref('');
const newIcon = ref('queue_music');
const newVisibility = ref<MediaPlaylistVisibility>('private');

const open = computed(() => ui.playlistDrawerOpen);
const isMobile = ref(typeof window !== 'undefined' && window.innerWidth < 768);
const effectivePinned = computed(() => !isMobile.value && ui.playlistDrawerPinned);
const isOwner = computed(() => selectedPlaylist.value?.userId === authStore.user?.id);

const panelStyle = computed(() => {
  if (effectivePinned.value) return { width: '380px' };
  if (isMobile.value) return { top: '0', right: '0', bottom: '0', left: '0' };
  return { top: '60px', right: '16px', bottom: '16px', width: '380px' };
});

function close() {
  ui.closePlaylistDrawer();
}

function onResize() {
  isMobile.value = window.innerWidth < 768;
}

function visibilityButtonClass(active: boolean): string {
  return [
    'w-10 flex items-center justify-center transition-colors',
    active
      ? 'bg-primary-600 text-white'
      : 'bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600',
  ].join(' ');
}

function playlistRowClass(id: string): string {
  const active = selectedPlaylist.value?.id === id;
  return [
    'w-full flex items-center gap-3 px-4 py-2.5 transition-colors',
    active
      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40',
  ].join(' ');
}

function playlistMeta(playlist: MediaPlaylist): string {
  // A person is named by their name, else the address they are reached through —
  // never by the derived handle. `Owner`/`Lastnik` stays the last resort for the
  // case the owner is not in the payload at all.
  const owner = personName(playlist.owner, tt('Owner', 'Lastnik'));
  const visibility = playlist.visibility === 'shared' ? tt('Shared', 'V skupni rabi') : tt('Private', 'Zasebno');
  return `${visibility} • ${owner}`;
}

function formatDuration(duration?: number | null): string {
  if (!duration) return '';
  const minutes = Math.floor(duration / 60);
  const seconds = Math.floor(duration % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function getToken(): string {
  return authStore.token ?? localStorage.getItem('accessToken') ?? localStorage.getItem('token') ?? '';
}

function thumbnailUrl(itemId: string): string {
  return getMediaThumbnailUrl(itemId, getToken()) ?? '';
}

function queueThumb(item: MediaLibraryItem): string {
  if (item.thumb) return item.thumb;
  if (item.thumbnailPath || item.mediaType === 'video') return thumbnailUrl(item.id) || '';
  return '';
}

function queuePlay(index: number) {
  if (index === player.playerQueueIndex.value && player.browserPlaying.value) {
    player.toggleBrowserPlay();
    return;
  }
  player.playFromQueue(index);
}

function clearQueue() {
  player.clearQueue();
}

function cancelCreate() {
  creating.value = false;
  newName.value = '';
  newIcon.value = 'queue_music';
  newVisibility.value = 'private';
}

async function createPlaylist() {
  if (!newName.value.trim()) return;
  creatingPlaylist.value = true;
  try {
    await playlistStore.createPlaylist({
      name: newName.value.trim(),
      icon: newIcon.value,
      visibility: newVisibility.value,
    });
    cancelCreate();
  } finally {
    creatingPlaylist.value = false;
  }
}

async function playPlaylist(startIndex: number) {
  if (!selectedPlaylist.value) return;
  const mediaItems = selectedPlaylist.value.items.map((item) => item.mediaItem).filter(Boolean);
  await player.setQueueAndPlay(mediaItems, startIndex, getToken(), { nextTick });
  if (!effectivePinned.value) close();
}

async function toggleVisibility() {
  if (!selectedPlaylist.value || !isOwner.value) return;
  await playlistStore.updatePlaylist(selectedPlaylist.value.id, {
    visibility: selectedPlaylist.value.visibility === 'shared' ? 'private' : 'shared',
  });
}

async function deleteSelectedPlaylist() {
  if (!selectedPlaylist.value || !isOwner.value) return;
  const ok = await confirmBox(t('dialogs.playlistDeleteConfirm'));
  if (!ok) return;
  await playlistStore.deletePlaylist(selectedPlaylist.value.id);
}

async function moveItem(index: number, delta: number) {
  if (!selectedPlaylist.value || !isOwner.value) return;
  const nextItems = [...selectedPlaylist.value.items];
  const targetIndex = index + delta;
  if (targetIndex < 0 || targetIndex >= nextItems.length) return;
  const currentItem = nextItems[index];
  nextItems[index] = nextItems[targetIndex];
  nextItems[targetIndex] = currentItem;
  await playlistStore.reorderItems(
    selectedPlaylist.value.id,
    nextItems.map((item) => item.id),
  );
}

watch(open, (isOpen) => {
  if (isOpen) playlistStore.initialize().catch(() => {});
});

onMounted(() => {
  window.addEventListener('resize', onResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', onResize);
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.playlist-slide-enter-active,
.playlist-slide-leave-active {
  transition:
    transform 0.25s ease,
    opacity 0.2s;
}
.playlist-slide-enter-from {
  transform: translateX(100%);
  opacity: 0;
}
.playlist-slide-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.drawer-push-enter-active,
.drawer-push-leave-active {
  transition: width 0.25s ease;
  overflow: hidden;
}
.drawer-push-enter-from,
.drawer-push-leave-to {
  width: 0 !important;
}
</style>
